import { fetchAuthSession } from 'aws-amplify/auth';
import loggingService from '../../services/loggingService';

const API_BASE_URL = 'https://api.innoworx.ai/Stage';

/**
 * Retrieves the authentication token from AWS Cognito.
 * @returns {Promise<string|null>} The ID token or null if not available.
 */
const getAuthToken = async () => {
  try {
    const session = await fetchAuthSession();
    const idToken = session.tokens?.idToken?.toString();
    
    if (idToken) {
      console.log('Inventory API: Token obtained successfully');
      return idToken;
    }
    
    console.warn('Inventory API: No ID token found in session');
    return null;
  } catch (error) {
    console.error('Inventory API: Error fetching auth session:', error);
    return null;
  }
};

/**
 * Generic function to make authenticated API requests.
 * @param {string} endpoint - The API endpoint relative to the BASE_URL.
 * @param {string} method - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [body=null] - The request body for POST/PUT requests.
 * @returns {Promise<Response>} - The response object from the API.
 * @throws {Error} - Throws an error if the API request fails or returns a non-OK status.
 */
const makeApiRequest = async (endpoint, method = 'GET', body = null) => {
  const token = await getAuthToken();

  if (!token) {
    const error = new Error(
      'Authentication token is missing. Please ensure a user is logged in.'
    );
    error.code = 'AUTHENTICATION_FAILED';
    error.details =
      'No valid authentication token found. User may need to log in again.';
    throw error;
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
  };

  const config = {
    method: method,
    headers: headers,
  };

  if (body) {
    config.body = JSON.stringify(body);
  }

  try {
    console.log(`Making ${method} request to: ${API_BASE_URL}${endpoint}`);
    const response = await fetch(`${API_BASE_URL}${endpoint}`, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error(
        `API Error: ${response.status} ${response.statusText} for ${endpoint}`,
        errorData
      );

      const error = new Error(
        `API Error: ${response.status} - ${errorData.body ? errorData.body.error : 'Unknown error or no error message from API'}`
      );
      error.status = response.status;
      error.statusText = response.statusText;
      error.endpoint = endpoint;
      error.responseData = errorData;
      
      // Log API error to logging service
      loggingService.logError(
        'api_error_inventory',
        `Inventory API error: ${response.status} ${response.statusText} for ${endpoint}`,
        error,
        {
          endpoint,
          method,
          status: response.status,
          statusText: response.statusText,
          responseData: errorData,
        }
      ).catch(logError => {
        console.warn('[inventoryApi] Failed to log error:', logError);
      });
      
      throw error;
    }

    return response;
  } catch (error) {
    if (error.code === 'AUTHENTICATION_FAILED') {
      throw error;
    }

    console.error(`Network or Fetch Error for ${endpoint}:`, error);
    error.endpoint = endpoint;
    error.method = method;
    
    // Log network/fetch errors to logging service
    loggingService.logError(
      'api_error_inventory',
      `Inventory API network/fetch error for ${endpoint}`,
      error,
      {
        endpoint,
        method,
        errorType: error?.name || 'NetworkError',
      }
    ).catch(logError => {
      console.warn('[inventoryApi] Failed to log error:', logError);
    });
    
    throw error;
  }
};

/**
 * API utility functions for Inventory Management.
 */
export const inventoryApi = {
  /**
   * Fetches all inventory items with optional filtering parameters.
   * GET /inventory/items
   * @param {Object} params - Optional parameters for filtering inventory items
   * @param {number} [params.limit] - Maximum number of items to return
   * @param {string} [params.lastKey] - Pagination key for next page
   * @param {string} [params.siteId] - Filter by site ID
   * @param {string} [params.userId] - Filter by user ID
   * @param {string} [params.vehicleId] - Filter by vehicle ID
   * @param {string} [params.sourceId] - Direct location ID filter (mutually exclusive with site/user/vehicle)
   * @param {string} [params.status] - Filter by status
   * @param {string} [params.type] - Filter by type
   * @param {string} [params.sourceType] - Filter by source type
   * @param {string} [params.originalSourceId] - Filter by original source ID
   * @param {string} [params.createdBy] - Filter by creator
   * @param {string} [params.createdAfter] - Filter items created after this date
   * @param {string} [params.createdBefore] - Filter items created before this date
   * @param {string} [params.onboardedAfter] - Filter items onboarded after this date
   * @param {string} [params.onboardedBefore] - Filter items onboarded before this date
   * @param {string} [params.nameContains] - Filter by name containing this text
   * @param {string} [params.descriptionContains] - Filter by description containing this text
   * @returns {Promise<Object>} Object containing success status and data or error information
   * @throws {Error} - Throws an error if the API request fails
   */
  getAllInventoryItems: async ({
    limit,
    lastKey,
    siteId,
    userId,
    vehicleId,
    sourceId,
    status,
    type,
    sourceType,
    originalSourceId,
    createdBy,
    createdAfter,
    createdBefore,
    onboardedAfter,
    onboardedBefore,
    nameContains,
    descriptionContains,
  } = {}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (limit !== undefined && limit !== null) {
        queryParams.append('limit', limit.toString());
      }
      if (lastKey !== undefined && lastKey !== null) {
        const lastKeyParam =
          typeof lastKey === 'string' ? lastKey : JSON.stringify(lastKey);
        queryParams.append('last_key', lastKeyParam);
      }
      if (siteId !== undefined && siteId !== null) {
        queryParams.append('site_id', siteId);
      }
      if (userId !== undefined && userId !== null) {
        queryParams.append('user_id', userId);
      }
      if (vehicleId !== undefined && vehicleId !== null) {
        queryParams.append('vehicle_id', vehicleId);
      }
      if (sourceId !== undefined && sourceId !== null) {
        queryParams.append('source_id', sourceId);
      }
      if (status !== undefined && status !== null) {
        queryParams.append('status', status);
      }
      if (type !== undefined && type !== null) {
        queryParams.append('type', type);
      }
      if (sourceType !== undefined && sourceType !== null) {
        queryParams.append('source_type', sourceType);
      }
      if (originalSourceId !== undefined && originalSourceId !== null) {
        queryParams.append('original_source_id', originalSourceId);
      }
      if (createdBy !== undefined && createdBy !== null) {
        queryParams.append('created_by', createdBy);
      }
      if (createdAfter !== undefined && createdAfter !== null) {
        queryParams.append('created_after', createdAfter);
      }
      if (createdBefore !== undefined && createdBefore !== null) {
        queryParams.append('created_before', createdBefore);
      }
      if (onboardedAfter !== undefined && onboardedAfter !== null) {
        queryParams.append('onboarded_after', onboardedAfter);
      }
      if (onboardedBefore !== undefined && onboardedBefore !== null) {
        queryParams.append('onboarded_before', onboardedBefore);
      }
      if (nameContains !== undefined && nameContains !== null) {
        queryParams.append('name_contains', nameContains);
      }
      if (descriptionContains !== undefined && descriptionContains !== null) {
        queryParams.append('description_contains', descriptionContains);
      }

      // Build endpoint with query parameters
      const endpoint = `/inventory/items${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      console.log('Inventory API: Making request to:', `${API_BASE_URL}${endpoint}`);

      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Inventory API: Response status:', response.status);
      console.log('Inventory API: Response data:', responseData);

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      console.error('Inventory API: Error in getAllInventoryItems:', error);
      
      // Handle different types of errors
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      // Handle API errors
      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      // Handle network errors
      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch inventory items',
      };
    }
  },

  /**
   * Fetches a single inventory item by ID.
   * GET /inventory/items/{itemId}
   * @param {string} itemId - The ID of the inventory item to fetch
   * @returns {Promise<Object>} Object containing success status and item data or error information
   */
  getInventoryItem: async (itemId) => {
    try {
      const endpoint = `/inventory/items/${itemId}`;
      console.log('Inventory API: Fetching item:', `${API_BASE_URL}${endpoint}`);

      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Inventory API: Item response:', responseData);

      // Handle response structure - item may be nested under 'item' key
      const itemData = responseData?.item || responseData;

      return {
        success: true,
        data: itemData,
        rawResponse: responseData, // Keep raw response for reference
      };
    } catch (error) {
      console.error('Inventory API: Error in getInventoryItem:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch inventory item',
      };
    }
  },

  /**
   * Downloads QR code for an inventory item.
   * GET /inventory/items/{itemId}/qr
   * @param {string} itemId - The ID of the inventory item
   * @returns {Promise<Object>} Object containing QR code image URL
   */
  downloadInventoryItemQR: async (itemId) => {
    try {
      const endpoint = `/inventory/items/${itemId}/qr`;
      console.log('Inventory API: Downloading QR code:', `${API_BASE_URL}${endpoint}`);

      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Inventory API: QR code response:', responseData);

      // Extract QR image URL from response
      const qrImageUrl = responseData.qr_image_url || 
                        responseData.qrImageUrl || 
                        responseData.data?.qr_image_url;

      return {
        success: true,
        data: responseData,
        qr_image_url: qrImageUrl,
      };
    } catch (error) {
      console.error('Inventory API: Error in downloadInventoryItemQR:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to download QR code',
      };
    }
  },

  /**
   * Fetches inventory transaction ledger/logs for an item.
   * GET /inventory/ledger
   * @param {Object} params - Parameters for filtering ledger entries
   * @param {string} [params.itemId] - Filter by specific inventory item UUID
   * @param {number} [params.limit] - Max entries per page (1-100, default: 20)
   * @param {string} [params.lastKey] - Pagination token for next page
   * @param {string} [params.transactionType] - Filter by transaction type (e.g., "transfer")
   * @param {string} [params.fromType] - Source location type (site|vehicle|employee)
   * @param {string} [params.fromId] - Source location ID
   * @param {string} [params.toType] - Destination location type (site|vehicle|employee)
   * @param {string} [params.toId] - Destination location ID
   * @param {string} [params.userId] - Filter by user who performed the action
   * @param {string} [params.notesContains] - Search text in transaction notes
   * @param {string} [params.startDate] - Filter entries after this date (ISO format)
   * @param {string} [params.endDate] - Filter entries before this date (ISO format)
   * @returns {Promise<Object>} Object containing entries, count, has_more, and next_page_token
   */
  getInventoryLedger: async ({
    itemId,
    limit = 20,
    lastKey,
    transactionType,
    fromType,
    fromId,
    toType,
    toId,
    userId,
    notesContains,
    startDate,
    endDate,
  } = {}) => {
    try {
      // Build query parameters
      const queryParams = new URLSearchParams();

      if (itemId !== undefined && itemId !== null) {
        queryParams.append('item_id', itemId);
      }
      if (limit !== undefined && limit !== null) {
        queryParams.append('limit', limit.toString());
      }
      if (lastKey !== undefined && lastKey !== null) {
        queryParams.append('last_key', lastKey);
      }
      if (transactionType !== undefined && transactionType !== null) {
        queryParams.append('transaction_type', transactionType);
      }
      if (fromType !== undefined && fromType !== null) {
        queryParams.append('from_type', fromType);
      }
      if (fromId !== undefined && fromId !== null) {
        queryParams.append('from_id', fromId);
      }
      if (toType !== undefined && toType !== null) {
        queryParams.append('to_type', toType);
      }
      if (toId !== undefined && toId !== null) {
        queryParams.append('to_id', toId);
      }
      if (userId !== undefined && userId !== null) {
        queryParams.append('user_id', userId);
      }
      if (notesContains !== undefined && notesContains !== null) {
        queryParams.append('notes_contains', notesContains);
      }
      if (startDate !== undefined && startDate !== null) {
        queryParams.append('start_date', startDate);
      }
      if (endDate !== undefined && endDate !== null) {
        queryParams.append('end_date', endDate);
      }

      // Build endpoint with query parameters
      const endpoint = `/inventory/ledger${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;

      console.log('Inventory API: Fetching ledger:', `${API_BASE_URL}${endpoint}`);

      const response = await makeApiRequest(endpoint, 'GET');
      const responseData = await response.json();

      console.log('Inventory API: Ledger response:', responseData);

      return {
        success: true,
        data: responseData,
        entries: responseData.entries || [],
        count: responseData.count || 0,
        scanned_count: responseData.scanned_count || 0,
        has_more: responseData.has_more || false,
        next_page_token: responseData.next_page_token || null,
      };
    } catch (error) {
      console.error('Inventory API: Error in getInventoryLedger:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to fetch inventory ledger',
      };
    }
  },

  /**
   * Updates inventory item status.
   * PUT /inventory/items/{itemId}
   * @param {string} itemId - The ID of the inventory item
   * @param {Object} updateData - Data to update
   * @param {string} [updateData.status] - New status (good_condition, under_maintenance, retired)
   * @param {string} [updateData.notes] - Notes for the status change
   * @returns {Promise<Object>} Object containing success status and updated item data
   */
  updateInventoryItemStatus: async (itemId, updateData) => {
    try {
      const endpoint = `/inventory/items/${itemId}`;
      console.log('Inventory API: Updating item status:', `${API_BASE_URL}${endpoint}`);

      const response = await makeApiRequest(endpoint, 'PUT', updateData);
      const responseData = await response.json();

      console.log('Inventory API: Update response:', responseData);

      // Handle response structure - item may be nested under 'item' key
      const itemData = responseData?.item || responseData;

      return {
        success: true,
        data: itemData,
        rawResponse: responseData,
      };
    } catch (error) {
      console.error('Inventory API: Error in updateInventoryItemStatus:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to update inventory item status',
      };
    }
  },

  /**
   * Starts maintenance for an inventory item.
   * POST /inventory/maintenance/start
   * @param {Object} maintenanceData - Maintenance data
   * @param {string} maintenanceData.itemId - The ID of the inventory item
   * @param {string} maintenanceData.maintenanceReason - Reason for maintenance
   * @param {string} maintenanceData.assignedTechnician - Assigned technician/vendor
   * @param {string} maintenanceData.technicianAddress - Address of technician/vendor
   * @param {string} maintenanceData.responsibleRole - Role of responsible person
   * @param {string} maintenanceData.responsiblePerson - Display name of responsible person
   * @param {string} maintenanceData.submissionDate - Date of submission (ISO format)
   * @param {string} maintenanceData.scheduledReturnDate - Scheduled return date (ISO format)
   * @param {string} [maintenanceData.maintenanceImageUrl] - URL of maintenance image
   * @param {string} [maintenanceData.status] - Status (default: 'submitted')
   * @returns {Promise<Object>} Object containing success status and response data
   */
  startMaintenance: async (maintenanceData) => {
    try {
      const endpoint = `/inventory/maintenance/start`;
      console.log('Inventory API: Starting maintenance:', `${API_BASE_URL}${endpoint}`);

      const requestBody = {
        item_id: maintenanceData.itemId,
        maintenance_reason: maintenanceData.maintenanceReason,
        assigned_technician: maintenanceData.assignedTechnician,
        technician_address: maintenanceData.technicianAddress,
        responsible_role: maintenanceData.responsibleRole,
        responsible_person: maintenanceData.responsiblePerson,
        submission_date: maintenanceData.submissionDate,
        scheduled_return_date: maintenanceData.scheduledReturnDate,
        status: maintenanceData.status || 'submitted',
        ...(maintenanceData.maintenanceImageUrl ? { maintenance_image_url: maintenanceData.maintenanceImageUrl } : {}),
      };

      const response = await makeApiRequest(endpoint, 'POST', requestBody);
      const responseData = await response.json();

      console.log('Inventory API: Start maintenance response:', responseData);

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      console.error('Inventory API: Error in startMaintenance:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to start maintenance',
      };
    }
  },

  /**
   * Completes maintenance for an inventory item.
   * POST /inventory/maintenance/complete
   * @param {Object} completionData - Maintenance completion data
   * @param {string} completionData.itemId - The ID of the inventory item
   * @param {string} completionData.maintenanceRemarks - Maintenance remarks/notes
   * @param {string} [completionData.actualReturnDate] - Actual return date (ISO format)
   * @param {number} [completionData.maintenanceCharges] - Maintenance charges
   * @param {number} [completionData.transportationCharges] - Transportation charges
   * @param {string} [completionData.safetyMeasures] - Safety measures taken
   * @param {string} [completionData.maintenanceImageUrl] - URL of maintenance return image
   * @returns {Promise<Object>} Object containing success status and response data
   */
  completeMaintenance: async (completionData) => {
    try {
      const endpoint = `/inventory/maintenance/complete`;
      console.log('Inventory API: Completing maintenance:', `${API_BASE_URL}${endpoint}`);

      const requestBody = {
        item_id: completionData.itemId,
        maintenance_remarks: completionData.maintenanceRemarks,
        ...(completionData.actualReturnDate ? { actual_return_date: completionData.actualReturnDate } : {}),
        ...(completionData.maintenanceCharges && completionData.maintenanceCharges > 0 ? { maintenance_charges: completionData.maintenanceCharges } : {}),
        ...(completionData.transportationCharges && completionData.transportationCharges > 0 ? { transportation_charges: completionData.transportationCharges } : {}),
        ...(completionData.safetyMeasures ? { safety_measures: completionData.safetyMeasures } : {}),
        ...(completionData.maintenanceImageUrl ? { maintenance_image_url: completionData.maintenanceImageUrl } : {}),
      };

      const response = await makeApiRequest(endpoint, 'POST', requestBody);
      const responseData = await response.json();

      console.log('Inventory API: Complete maintenance response:', responseData);

      return {
        success: true,
        data: responseData,
      };
    } catch (error) {
      console.error('Inventory API: Error in completeMaintenance:', error);
      
      if (error.code === 'AUTHENTICATION_FAILED') {
        return {
          success: false,
          error: 'Authentication failed',
          message: 'Authentication token is missing. Please log in again.',
        };
      }

      if (error.status) {
        return {
          success: false,
          error: error.responseData?.error || 'API Error',
          message: error.responseData?.message || `Request failed with status ${error.status}`,
        };
      }

      return {
        success: false,
        error: 'Network error',
        message: error.message || 'Failed to complete maintenance',
      };
    }
  },
};
